import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const features = [
  {
    icon: 'BrainCircuit',
    title: 'AI-Generated Touchpoints',
    description: 'Personalized monthly messages written by AI — tailored to each client relationship automatically.',
  },
  {
    icon: 'MessageSquare',
    title: 'Local Area Code SMS',
    description: 'Every message sends from a local number matching your client\'s area code for higher open rates.',
  },
  {
    icon: 'Shield',
    title: 'A2P 10DLC Compliant',
    description: 'Fully registered with carrier requirements. Your messages reach inboxes, not spam filters.',
  },
  {
    icon: 'RefreshCw',
    title: 'Set It & Forget It',
    description: 'Onboard a client once and the platform handles follow-ups for years — market updates, check-ins, and more.',
  },
];

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user?.id) {
      navigate('/client-management');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Nurture Buddy.ai – AI-Powered Real Estate Client Relationships</title>
        <meta name="description" content="Automate your real estate follow-ups with AI. Stay top-of-mind with clients through personalized SMS touchpoints. A2P compliant, local area codes, set it and forget it." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(74,222,128,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.5) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.07]"
            style={{ background: 'radial-gradient(circle, #4ade80 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-[0.05]"
            style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative">
          {/* Header */}
          <header className="flex items-center justify-between px-6 py-6 lg:px-12">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/logo-icon.png"
                alt="Nurture Buddy.ai"
                className="w-10 h-10 rounded-xl object-cover glow-primary"
              />
              <span className="font-heading text-xl font-semibold text-gradient">Nurture Buddy.ai</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth"
              >
                Get Started
              </Link>
            </nav>
          </header>

          {/* Hero */}
          <section className="px-6 py-16 lg:py-24 lg:px-12 text-center max-w-4xl mx-auto">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight">
              Stay top-of-mind with clients,{' '}
              <span className="text-gradient">without the effort.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Nurture Buddy.ai automates the touchpoints that turn past clients into repeat business and referrals. AI-generated messages, local area codes, A2P compliant.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="w-full sm:w-auto px-8 py-3 rounded-xl font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth glow-primary"
              >
                Start Free
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto px-8 py-3 rounded-xl font-medium border border-border hover:border-primary/50 hover:bg-primary/5 transition-smooth"
              >
                Sign in
              </Link>
            </div>
          </section>

          {/* Features */}
          <section className="px-6 py-16 lg:py-24 lg:px-12">
            <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-6">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="flex items-start gap-4 p-6 rounded-2xl transition-smooth hover:elevation-3 border border-border bg-card/90"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-primary/10 border border-primary/20"
                  >
                    <Icon name={f.icon} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{f.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Trust signals */}
          <section className="px-6 py-12 lg:px-12">
            <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-8 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={18} className="text-success" />
                <span>SSL Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={18} className="text-success" />
                <span>TCPA Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={18} className="text-success" />
                <span>A2P 10DLC Registered</span>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="px-6 py-16 lg:py-24 lg:px-12">
            <div
              className="max-w-3xl mx-auto rounded-2xl p-8 sm:p-12 text-center bg-card/90 border border-primary/20 glow-sm"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                Ready to nurture your pipeline?
              </h2>
              <p className="mt-3 text-muted-foreground">
                Join agents who stay connected with clients on autopilot.
              </p>
              <Link
                to="/register"
                className="inline-block mt-6 px-8 py-3 rounded-xl font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth"
              >
                Get Started Free
              </Link>
            </div>
          </section>

          {/* Footer */}
          <footer className="px-6 py-8 lg:px-12 border-t border-border">
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} Nurture Buddy.ai</span>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <Link to="/privacy-policy" className="hover:text-foreground transition-smooth">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-foreground transition-smooth">Terms of Service</Link>
                <a href="mailto:support@nurturebuddy.ai" className="hover:text-foreground transition-smooth">Support</a>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default Landing;
